import {css, cx as addStyles} from '@emotion/css';
import React, {
  CSSProperties,
  HTMLAttributes,
  InputHTMLAttributes,
  LabelHTMLAttributes,
  StyleHTMLAttributes,
} from 'react';
import {maskPhone, maskZipcode, theme, unmask, withState} from './Common';

export type TextInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> & {
  label?: string;
  mask?: 'zipcode' | 'phone' | undefined;
  error?: Maybe<string>;
  setValue?: (value: string) => unknown;
  containerStyle?: StyleHTMLAttributes<HTMLDivElement>;
  containerClassName?: string;
  labelStyle?: StyleHTMLAttributes<HTMLDivElement>;
  labelClassName?: string;
  errorStyle?: StyleHTMLAttributes<HTMLDivElement>;
  errorClassName?: string;
  showErrorText?: boolean;
  inputStyle?: string;
  placeholderStyle?: CSSProperties;
};

export const TextInput = ({
  label,
  mask,
  error,
  setValue,
  containerStyle,
  containerClassName,
  labelStyle,
  labelClassName,
  errorStyle,
  errorClassName,
  name,
  onKeyDown,
  showErrorText,
  inputStyle,
  placeholderStyle,
  ...rest
}: TextInputProps) => {
  const keyDownHandler = event => {
    if (mask === 'zipcode') return maskZipcode(event);
    if (mask === 'phone') return maskPhone(event);
    if (mask === undefined) return unmask(event);
    return;
  };
  return (
    <InputContainer className={containerClassName} style={containerStyle}>
      {!!label && (
        <InputLabel htmlFor={name} className={labelClassName} style={labelStyle}>
          {label}
        </InputLabel>
      )}
      <Input
        onKeyDown={onKeyDown || keyDownHandler}
        onChange={setValue ? e => setValue(e.target.value) : undefined}
        inputStyle={inputStyle}
        placeholderStyle={placeholderStyle}
        {...{error, id: label ? name : undefined}}
        {...rest}
      />
      {!!error && showErrorText && (
        <InputError className={errorClassName} style={errorStyle}>
          {error}
        </InputError>
      )}
    </InputContainer>
  );
};
const InputContainer = ({className, ...rest}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
      }),
      className,
    )}
    {...rest}
  />
);
const InputLabel = ({className, ...rest}: LabelHTMLAttributes<HTMLLabelElement>) => (
  <label
    className={addStyles(
      textStyle,
      css({width: '100%', cursor: 'pointer', fontWeight: theme.font.bold}),
      className,
    )}
    {...rest}
  />
);
const Input = ({
  error,
  className,
  inputStyle,
  disabled,
  placeholderStyle,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & {
  error?: Maybe<string>;
  inputStyle?: string;
  placeholderStyle?: CSSProperties;
}) => (
  <input
    className={addStyles(
      textStyle,
      css({
        fontSize: theme.font.sizes.medium,
        width: '100%',
        background: theme.colors.white,
        height: '53px',
        padding: `0 ${theme.spacings.xxsmall}`,
        border: `1px solid ${theme.colors.darkBlue}`,
        margin: theme.spacings.xxsmall,
        outline: 'none',
        ':focus-within': {boxShadow: `0 1px 6px #2e319229`},
        ':hover': {boxShadow: `0px 1px 4px #2e319266`},
        '::placeholder': {color: 'inherit', opacity: 0.5, ...placeholderStyle},
      }),
      inputStyle,
      {[disabledStyle]: disabled},
      {[errorStyle]: !!error},
      className,
    )}
    disabled={disabled}
    {...rest}
  />
);
const InputError = ({className, ...rest}: HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={addStyles(
      textStyle,
      css({
        width: '100%',
        textAlign: 'left',
        color: theme.colors.red,
        [theme.mediaMaxWidth.small]: {textAlign: 'center'},
      }),
      className,
    )}
    {...rest}
  />
);
const textStyle = css({
  fontFamily: theme.font.family,
  color: theme.colors.darkBlue,
  fontSize: theme.font.sizes.small,
  fontWeight: theme.font.normal,
  textAlign: 'left',
});
const disabledStyle = css({cursor: 'not-allowed', opacity: 0.5, '&:hover': {boxShadow: 'none'}});
const errorStyle = css({
  borderColor: theme.colors.red,
  color: theme.colors.red,
  [theme.mediaMaxWidth.small]: {marginBottom: '30px'},
});

export const StatefulTextInput = withState(TextInput, '');
